import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { rem, Button, Spacer, AppView, CardView, SettingsItemInput, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, SettingsItemInputMask, Separator, } from 'design-system';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Squircle from 'design-system/Squircle';
import SectionHeader from 'design-system/SectionHeader';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { IconBank, IconEmailWithStar, IconUnion } from 'design-system/icons';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import Sentry from 'utils/sentry';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAutoFocusInput } from 'hooks/useAutoFocusInput';
import { validAccountNumber, validSortCode } from 'utils/validation';
import { transferProductString } from 'features/invest/constants';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import Amplitude from 'utils/amplitude';
import { amplitudeSource } from '../utils/analytics';
import { isWeb } from '../../../constants';
const strings = {
    sortCode: 'Sort code',
    sortCodePlaceholder: 'e.g. 12-34-56',
    accountNumber: 'Account number',
    accountNumberPlaceholder: 'e.g. 12345678',
    title: (t, type, supportsPayUk) => t === 'JISA'
        ? `What’s your child’s Junior ${transferProductString[type]} ISA account ${supportsPayUk ? 'details' : 'reference'}?`
        : `What’s your ${transferProductString[type]} ISA account ${supportsPayUk ? 'details' : 'reference'}?`,
    subTitle: (supportsPayUk) => `Enter the exact account ${supportsPayUk ? 'details' : 'reference'} from your current provider to avoid rejection`,
    subTitleJISA: (supportsPayUk) => `Enter the exact account ${supportsPayUk ? 'details' : 'reference'} from your current child’s provider to avoid rejection`,
    accountReference: 'Account reference',
    accountNumberOrReference: 'Account number or reference',
    enter: 'e.g. AB123456A',
    eg: (s) => `e.g. ${s}`,
    mustMatchExactly: 'This must match your provider’s records exactly. ', // space intentional
    howToFindIt: 'How\xa0to\xa0find\xa0it?',
    howToFindItDescription: (t, s, hasHintOrExample, type) => t === 'JISA'
        ? `You’ll find your account reference in emails or letters from ${s} ${'Junior ISA'}.${hasHintOrExample ? ' It might look like the one below.' : ''}`
        : `You’ll find your account reference in emails or letters from ${s} ${transferProductString[type]} ${t}.${hasHintOrExample ? ' It might look like the one below.' : ''}`,
    whatItLooksLike: 'What it might look like',
    hintPrimaryFallback: 'Hint',
    hintSecondaryFallback: 'Example',
    cannotFindIt: 'Can’t find it?',
    callOrEmail: (t, s, type) => t === 'JISA' ? `Call or email ${s} Junior ISA` : `Call or email ${s} ${transferProductString[type]} ${t}`,
    callOrEmailDescription: 'You can find their phone number and email address on their website.',
    continue: 'Continue',
    okay: 'Okay',
};
function normalizeAccountReference(s, refRegex) {
    let normalized = s.trim();
    if (refRegex) {
        const matched = normalized.match(refRegex);
        if (!matched)
            return null;
        [normalized] = matched;
    }
    if (!normalized)
        return null;
    return normalized;
}
const HowToFindItModalContent = React.memo(({ accountType, providerName, refHint, refExample, type, supportsPayUk }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const hideModal = useCallback(() => {
        Modal.hide();
    }, []);
    return (<>
      <Spacer h={16}/>
      <Text TextThin-14 Secondary centered>
        {strings.howToFindItDescription(accountType, providerName, !!(refHint || refExample), type)}
      </Text>
      {refHint || refExample || supportsPayUk ? (<>
          <SectionHeader largeVerticalMargins>{strings.whatItLooksLike}</SectionHeader>
          {!!supportsPayUk && (<>
              <CardView noMargin style={styles.modalCard}>
                <View style={styles.modalCardRow}>
                  <Squircle color={colors.cards.onDark}>
                    <IconBank gradientKey="skyBlue"/>
                  </Squircle>
                  <View style={styles.flex}>
                    <Text Text-16>12-34-56</Text>
                    <Text TextThin-14 Secondary>
                      Sort code is 6 digits
                    </Text>
                  </View>
                </View>
              </CardView>
              <Spacer h={16}/>
              <CardView noMargin style={styles.modalCard}>
                <View style={styles.modalCardRow}>
                  <Squircle color={colors.cards.onDark}>
                    <IconUnion gradientKey="skyBlue"/>
                  </Squircle>
                  <View style={styles.flex}>
                    <Text Text-16>12345678</Text>
                    <Text TextThin-14 Secondary>
                      Account number is 8 digits
                    </Text>
                  </View>
                </View>
              </CardView>
              {!!(refHint || refExample) && (<>
                  <Spacer h={16}/>
                  <View style={styles.orContainerModal}>
                    <View style={styles.flex}>
                      <Separator style={styles.separator}/>
                    </View>

                    <Text TextThin-14 Secondary>
                      or
                    </Text>

                    <View style={styles.flex}>
                      <Separator style={styles.separator}/>
                    </View>
                  </View>
                  <Spacer h={16}/>
                </>)}
            </>)}
          {!!(refHint || refExample) && (<CardView noMargin style={styles.modalCard}>
              <View style={styles.modalCardRow}>
                <Squircle color={colors.cards.onDark}>
                  <IconUnion gradientKey="skyBlue"/>
                </Squircle>
                <View style={styles.flex}>
                  <Text Text-16>{refExample || strings.hintPrimaryFallback}</Text>
                  <Text TextThin-14 Secondary>
                    {refHint || strings.hintSecondaryFallback}
                  </Text>
                </View>
              </View>
            </CardView>)}
        </>) : null}
      <SectionHeader largeVerticalMargins>{strings.cannotFindIt}</SectionHeader>
      <CardView noMargin style={styles.modalCard}>
        <View style={styles.modalCardRow}>
          <Squircle color={colors.cards.onDark}>
            <IconEmailWithStar gradientKey="peach"/>
          </Squircle>
          <View style={styles.flex}>
            <Text Text-16>{strings.callOrEmail(accountType, providerName, type)}</Text>
            <Text TextThin-14 Secondary>
              {strings.callOrEmailDescription}
            </Text>
          </View>
        </View>
      </CardView>
      <Spacer.H24 />
      <Button brand title={strings.okay} onPress={hideModal}/>
    </>);
});
HowToFindItModalContent.displayName = 'HowToFindItModalContent';
const sortCodeMask = { mask: '99-99-99' };
const ProductTransferAccountReferenceScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const { type, provider, accountId } = route.params;
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const accountType = accountId ? 'JISA' : 'ISA';
    const supportsPayUk = type === 'CASH' && (accountType === 'ISA' ? provider.isaSupportsPayUk : provider.jisaSupportsPayUk);
    const refRegexSource = accountType === 'ISA' ? provider.isaRefRegex : provider.jisaRefRegex;
    const refHint = accountType === 'ISA' ? provider.isaRefHint : provider.jisaRefHint;
    const refSample = accountType === 'ISA' ? provider.isaRefSample : provider.jisaRefSample;
    const inputRef = useRef(null);
    const sortCodeRef = useRef(null);
    const accountNumberRef = useRef(null);
    useAutoFocusInput(supportsPayUk ? undefined : inputRef);
    useEffect(() => {
        Amplitude.logEvent('Transfer Provider Details Screen', {
            source: amplitudeSource,
            account: accountId ? 'jisa' : 'isa',
        });
    }, [accountId]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const [inputValue, setInputValue] = useState('');
    const [sortCode, setSortCode] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const onChangeText = useCallback((newText) => {
        setInputValue(newText);
    }, []);
    const onChangeSortCode = useCallback((newText) => {
        setSortCode(newText);
    }, []);
    const onChangeAccountNumber = useCallback((newText) => {
        setAccountNumber(newText.replace(/\D/g, ''));
    }, []);
    const refRegex = useMemo(() => {
        if (!refRegexSource)
            return null;
        try {
            return new RegExp(refRegexSource);
        }
        catch (err) {
            Sentry.logError({
                message: 'Could not compile regex (ignored)',
                data: { refRegex: refRegexSource },
                filename: 'ProductTransferAccountReferenceScreen',
                err,
            });
            return null;
        }
    }, [refRegexSource]);
    const accountReference = useMemo(() => normalizeAccountReference(inputValue.toUpperCase(), refRegex), [inputValue, refRegex]);
    const isSortCodeValid = useMemo(() => validSortCode(sortCode), [sortCode]);
    const isAccountNumberValid = useMemo(() => validAccountNumber(accountNumber), [accountNumber]);
    const canUsePayUk = isSortCodeValid && isAccountNumberValid;
    const accountReferenceDescription = accountType === 'ISA' && !supportsPayUk ? strings.accountNumberOrReference : strings.accountReference;
    const handleContinue = useCallback(() => {
        if (!accountReference && !canUsePayUk)
            return;
        Keyboard.dismiss();
        if (accountReference && sortCode && accountNumber) {
            Modal.showConfirmation({
                title: 'Check your account details',
                subtitle: 'Enter either your sort code and account number, or your account reference, not both.',
                confirmButtonTitle: 'Okay',
            });
            return;
        }
        Modal.hide();
        navigation.navigate('ProductTransferBalance', {
            type,
            provider,
            accountId,
            ...(canUsePayUk
                ? { previousProviderSortCode: sortCode, previousProviderAccountNumber: accountNumber }
                : { accountReference: accountReference }),
        });
    }, [accountReference, canUsePayUk, type, provider, accountId, sortCode, accountNumber]);
    const showHowToFindIt = useCallback(() => {
        Keyboard.dismiss();
        Modal.show(<HowToFindItModalContent type={type} accountType={accountType} supportsPayUk={supportsPayUk} refHint={refHint} refExample={refSample} providerName={provider.shortName || provider.officialName}/>, { title: strings.howToFindIt });
    }, [accountType, refHint, refSample, provider.officialName, provider.shortName, type, supportsPayUk]);
    return (<AppView withFrame={false}>
          <KeyboardAwareScrollView mode="layout" onScroll={scrollHandler} scrollEventThrottle={16} bottomOffset={HEIGHT_PLUS_PADDING} keyboardShouldPersistTaps="handled" contentContainerStyle={paddingHorizontalStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
            <ScreenInfoHeader title={strings.title(accountType, type, supportsPayUk)} subTitle={accountType === 'JISA' ? strings.subTitleJISA(supportsPayUk) : strings.subTitle(supportsPayUk)}/>
            {!!supportsPayUk && (<>
                <CardView noMarginTop>
                  <SettingsItemInputMask type="custom" maxLength={8} value={sortCode} ref={sortCodeRef} returnKeyType="next" options={sortCodeMask} keyboardType="number-pad" description={strings.sortCode} onChangeText={onChangeSortCode} submitBehavior="submit" placeholder={strings.sortCodePlaceholder} icon={<IconBank gradientKey="skyBlue"/>} onSubmitEditing={() => accountNumberRef.current?.focus()} onClearTextInput={() => {
                setSortCode('');
            }}/>
                </CardView>
                <CardView noMarginTop>
                  <SettingsItemInput maxLength={8} value={accountNumber} ref={accountNumberRef} returnKeyType="done" keyboardType="number-pad" onSubmitEditing={handleContinue} description={strings.accountNumber} onChangeText={onChangeAccountNumber} icon={<IconUnion gradientKey="skyBlue"/>} placeholder={strings.accountNumberPlaceholder}/>
                </CardView>
                <View style={styles.orContainer}>
                  <View style={styles.flex}>
                    <Separator style={styles.separator}/>
                  </View>

                  <Text TextThin-14 Secondary>
                    or
                  </Text>

                  <View style={styles.flex}>
                    <Separator style={styles.separator}/>
                  </View>
                </View>
                <Spacer h={16}/>
              </>)}

            <CardView noMarginTop noMarginBottom>
              <SettingsItemInput ref={inputRef} value={inputValue} onChangeText={onChangeText} autoCapitalize="characters" icon={<IconUnion gradientKey="skyBlue"/>} description={accountReferenceDescription} returnKeyType="done" placeholder={refSample ? strings.eg(refSample) : strings.enter}/>
            </CardView>

            <View style={styles.inputInfo}>
              <Text TextThin-14 Secondary>
                {strings.mustMatchExactly}
                <Text Text-14 Primary onPress={showHowToFindIt} suppressHighlighting>
                  {strings.howToFindIt}
                </Text>
              </Text>
            </View>
          </KeyboardAwareScrollView>

          <KeyboardAvoidingButton brand title={strings.continue} onPress={handleContinue} disabled={accountReference == null && !canUsePayUk}/>
        </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    inputInfo: {
        margin: rem(16),
    },
    modalCard: {
        backgroundColor: colors.background.dark,
    },
    modalCardRow: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: rem(16),
        gap: rem(16),
    },
    flex: {
        flex: 1,
    },
    orContainer: {
        gap: rem(18),
        height: rem(21),
        alignItems: 'center',
        flexDirection: 'row',
        paddingHorizontal: rem(16),
    },
    orContainerModal: {
        gap: rem(18),
        height: rem(21),
        alignItems: 'center',
        flexDirection: 'row',
    },
    separator: {
        backgroundColor: colors.borders.divider2,
    },
}));
export default ProductTransferAccountReferenceScreen;
